import { baseQueriesApi as api } from "./base";
export const addTagTypes = [
  "authentication",
  "drivers",
  "companies",
  "products",
  "vehicles",
  "transport_orders",
  "forwarding_orders",
  "waybiller",
  "locations",
  "contracts",
  "contract-templates",
  "history",
  "deliveries",
  "comments",
] as const;
const commentParentToTag: Record<
  CommentCreate["table"],
  (typeof addTagTypes)[number][]
> = {
  Company: ["companies"],
  Contract: ["contracts"],
  Driver: ["drivers"],
  ForwardingOrder: ["forwarding_orders"],
  Location: ["locations"],
  Product: ["products"],
  TransportOrder: ["transport_orders"],
};
const commentAllPossibleTags: (typeof addTagTypes)[number][] = [
  "comments",
  "history",
  ...Object.values(commentParentToTag).flat(),
];
const invalidatesCommentTags = (
  _: unknown,
  __: unknown,
  arg: CreateCommentApiCommentsPostApiArg,
): (typeof addTagTypes)[number][] => [
  "comments",
  "history",
  ...(commentParentToTag[arg.commentCreate.table] ?? []),
];
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      loginForAccessTokenApiTokenPost: build.mutation<
        LoginForAccessTokenApiTokenPostApiResponse,
        LoginForAccessTokenApiTokenPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/token`,
          method: "POST",
          body: queryArg.tokenRequest,
        }),
        invalidatesTags: ["authentication"],
      }),
      loginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPost:
        build.mutation<
          LoginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPostApiResponse,
          LoginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPostApiArg
        >({
          query: (queryArg) => ({
            url: `/api/organizations/${queryArg.organizationId}/token`,
            method: "POST",
          }),
          invalidatesTags: ["authentication"],
        }),
      getResourceTokenApiResourceTokenResourceResourceIdPost: build.mutation<
        GetResourceTokenApiResourceTokenResourceResourceIdPostApiResponse,
        GetResourceTokenApiResourceTokenResourceResourceIdPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/resource-token/${queryArg.resource}/${queryArg.resourceId}`,
          method: "POST",
        }),
        invalidatesTags: ["authentication"],
      }),
      getCurrentUserInfoApiMeGet: build.query<
        GetCurrentUserInfoApiMeGetApiResponse,
        GetCurrentUserInfoApiMeGetApiArg
      >({
        query: () => ({ url: `/api/me` }),
        providesTags: ["authentication"],
      }),
      getUsersApiUsersGet: build.query<
        GetUsersApiUsersGetApiResponse,
        GetUsersApiUsersGetApiArg
      >({
        query: () => ({ url: `/api/users` }),
        providesTags: ["authentication"],
      }),
      getOrganizationalUsersApiOrganizationalUsersGet: build.query<
        GetOrganizationalUsersApiOrganizationalUsersGetApiResponse,
        GetOrganizationalUsersApiOrganizationalUsersGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizational-users`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            email: queryArg.email,
            is_active: queryArg.isActive,
            is_manager: queryArg.isManager,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["authentication"],
      }),
      listDriversApiDriversGet: build.query<
        ListDriversApiDriversGetApiResponse,
        ListDriversApiDriversGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drivers`,
          params: {
            include_orders: queryArg.includeOrders,
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            first_name: queryArg.firstName,
            last_name: queryArg.lastName,
            phone: queryArg.phone,
            personal_code: queryArg.personalCode,
            manager_id: queryArg.managerId,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["drivers"],
      }),
      createDriverApiDriversPost: build.mutation<
        CreateDriverApiDriversPostApiResponse,
        CreateDriverApiDriversPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drivers`,
          method: "POST",
          body: queryArg.driverCreate,
        }),
        invalidatesTags: ["drivers", "history"],
      }),
      getDriverApiDriversDriverIdGet: build.query<
        GetDriverApiDriversDriverIdGetApiResponse,
        GetDriverApiDriversDriverIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/drivers/${queryArg.driverId}` }),
        providesTags: ["drivers"],
      }),
      updateDriverApiDriversDriverIdPut: build.mutation<
        UpdateDriverApiDriversDriverIdPutApiResponse,
        UpdateDriverApiDriversDriverIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drivers/${queryArg.driverId}`,
          method: "PUT",
          body: queryArg.driverUpdate,
        }),
        invalidatesTags: ["drivers", "history"],
      }),
      deleteDriverApiDriversDriverIdDelete: build.mutation<
        DeleteDriverApiDriversDriverIdDeleteApiResponse,
        DeleteDriverApiDriversDriverIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drivers/${queryArg.driverId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["drivers", "history"],
      }),
      listCompaniesApiCompaniesGet: build.query<
        ListCompaniesApiCompaniesGetApiResponse,
        ListCompaniesApiCompaniesGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/companies`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            name: queryArg.name,
            registry_code: queryArg.registryCode,
            is_supplier: queryArg.isSupplier,
            is_transporter: queryArg.isTransporter,
            is_buyer: queryArg.isBuyer,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["companies"],
      }),
      createCompanyApiCompaniesPost: build.mutation<
        CreateCompanyApiCompaniesPostApiResponse,
        CreateCompanyApiCompaniesPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/companies`,
          method: "POST",
          body: queryArg.companyCreate,
        }),
        invalidatesTags: ["companies", "history"],
      }),
      getCompanyApiCompaniesCompanyIdGet: build.query<
        GetCompanyApiCompaniesCompanyIdGetApiResponse,
        GetCompanyApiCompaniesCompanyIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/companies/${queryArg.companyId}` }),
        providesTags: ["companies"],
      }),
      updateCompanyApiCompaniesCompanyIdPut: build.mutation<
        UpdateCompanyApiCompaniesCompanyIdPutApiResponse,
        UpdateCompanyApiCompaniesCompanyIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/companies/${queryArg.companyId}`,
          method: "PUT",
          body: queryArg.companyUpdate,
        }),
        invalidatesTags: ["companies", "history"],
      }),
      deleteCompanyApiCompaniesCompanyIdDelete: build.mutation<
        DeleteCompanyApiCompaniesCompanyIdDeleteApiResponse,
        DeleteCompanyApiCompaniesCompanyIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/companies/${queryArg.companyId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["companies", "history"],
      }),
      listProductsApiProductsGet: build.query<
        ListProductsApiProductsGetApiResponse,
        ListProductsApiProductsGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            name: queryArg.name,
            sales_contract_template_id: queryArg.salesContractTemplateId,
            purchase_contract_template_id: queryArg.purchaseContractTemplateId,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["products"],
      }),
      createProductApiProductsPost: build.mutation<
        CreateProductApiProductsPostApiResponse,
        CreateProductApiProductsPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products`,
          method: "POST",
          body: queryArg.productCreate,
        }),
        invalidatesTags: ["products", "history"],
      }),
      getProductApiProductsProductIdGet: build.query<
        GetProductApiProductsProductIdGetApiResponse,
        GetProductApiProductsProductIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/products/${queryArg.productId}` }),
        providesTags: ["products"],
      }),
      updateProductApiProductsProductIdPut: build.mutation<
        UpdateProductApiProductsProductIdPutApiResponse,
        UpdateProductApiProductsProductIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/${queryArg.productId}`,
          method: "PUT",
          body: queryArg.productUpdate,
        }),
        invalidatesTags: ["products", "history"],
      }),
      deleteProductApiProductsProductIdDelete: build.mutation<
        DeleteProductApiProductsProductIdDeleteApiResponse,
        DeleteProductApiProductsProductIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/${queryArg.productId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["products", "history"],
      }),
      listVehiclesApiVehiclesGet: build.query<
        ListVehiclesApiVehiclesGetApiResponse,
        ListVehiclesApiVehiclesGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/vehicles`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            type: queryArg["type"],
            number: queryArg["number"],
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["vehicles"],
      }),
      createVehicleApiVehiclesPost: build.mutation<
        CreateVehicleApiVehiclesPostApiResponse,
        CreateVehicleApiVehiclesPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/vehicles`,
          method: "POST",
          body: queryArg.vehicleCreate,
        }),
        invalidatesTags: ["vehicles", "drivers", "history"],
      }),
      getVehicleApiVehiclesVehicleIdGet: build.query<
        GetVehicleApiVehiclesVehicleIdGetApiResponse,
        GetVehicleApiVehiclesVehicleIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/vehicles/${queryArg.vehicleId}` }),
        providesTags: ["vehicles"],
      }),
      updateVehicleApiVehiclesVehicleIdPut: build.mutation<
        UpdateVehicleApiVehiclesVehicleIdPutApiResponse,
        UpdateVehicleApiVehiclesVehicleIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.vehicleId}`,
          method: "PUT",
          body: queryArg.vehicleUpdate,
        }),
        invalidatesTags: ["vehicles", "drivers", "history"],
      }),
      deleteVehicleApiVehiclesVehicleIdDelete: build.mutation<
        DeleteVehicleApiVehiclesVehicleIdDeleteApiResponse,
        DeleteVehicleApiVehiclesVehicleIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/vehicles/${queryArg.vehicleId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["vehicles", "history"],
      }),
      listTransportOrdersApiTransportOrdersGet: build.query<
        ListTransportOrdersApiTransportOrdersGetApiResponse,
        ListTransportOrdersApiTransportOrdersGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/transport-orders`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            status: queryArg.status,
            entry_status: queryArg.entryStatus,
            loading_date_from: queryArg.loadingDateFrom,
            loading_date_to: queryArg.loadingDateTo,
            unloading_date_from: queryArg.unloadingDateFrom,
            unloading_date_to: queryArg.unloadingDateTo,
            loaded_weight_from: queryArg.loadedWeightFrom,
            loaded_weight_to: queryArg.loadedWeightTo,
            unloaded_weight_from: queryArg.unloadedWeightFrom,
            unloaded_weight_to: queryArg.unloadedWeightTo,
            purchase_price_from: queryArg.purchasePriceFrom,
            purchase_price_to: queryArg.purchasePriceTo,
            sales_price_from: queryArg.salesPriceFrom,
            sales_price_to: queryArg.salesPriceTo,
            transport_rate_from: queryArg.transportRateFrom,
            transport_rate_to: queryArg.transportRateTo,
            product_id: queryArg.productId,
            driver_id: queryArg.driverId,
            driver_manager_id: queryArg.driverManagerId,
            truck_id: queryArg.truckId,
            trailer_id: queryArg.trailerId,
            supplier_id: queryArg.supplierId,
            buyer_id: queryArg.buyerId,
            transporter_id: queryArg.transporterId,
            unloading_city: queryArg.unloadingCity,
            unloading_country: queryArg.unloadingCountry,
            loading_city: queryArg.loadingCity,
            loading_country: queryArg.loadingCountry,
            is_national: queryArg.isNational,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["transport_orders"],
      }),
      createTransportOrderApiTransportOrdersPost: build.mutation<
        CreateTransportOrderApiTransportOrdersPostApiResponse,
        CreateTransportOrderApiTransportOrdersPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/transport-orders`,
          method: "POST",
          body: queryArg.transportOrderIn,
        }),
        invalidatesTags: [
          "transport_orders",
          "deliveries",
          "contracts",
          "history",
        ],
      }),
      getTransportOrderApiTransportOrdersTransportOrderIdGet: build.query<
        GetTransportOrderApiTransportOrdersTransportOrderIdGetApiResponse,
        GetTransportOrderApiTransportOrdersTransportOrderIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/transport-orders/${queryArg.transportOrderId}`,
        }),
        providesTags: ["transport_orders"],
      }),
      updateTransportOrderApiTransportOrdersTransportOrderIdPut: build.mutation<
        UpdateTransportOrderApiTransportOrdersTransportOrderIdPutApiResponse,
        UpdateTransportOrderApiTransportOrdersTransportOrderIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/transport-orders/${queryArg.transportOrderId}`,
          method: "PUT",
          body: queryArg.transportOrderUpdate,
        }),
        invalidatesTags: [
          "transport_orders",
          "deliveries",
          "contracts",
          "history",
        ],
      }),
      deleteTransportOrderApiTransportOrdersTransportOrderIdDelete:
        build.mutation<
          DeleteTransportOrderApiTransportOrdersTransportOrderIdDeleteApiResponse,
          DeleteTransportOrderApiTransportOrdersTransportOrderIdDeleteApiArg
        >({
          query: (queryArg) => ({
            url: `/api/transport-orders/${queryArg.transportOrderId}`,
            method: "DELETE",
          }),
          invalidatesTags: [
            "transport_orders",
            "deliveries",
            "contracts",
            "history",
          ],
        }),
      listForwardingOrdersApiForwardingOrdersGet: build.query<
        ListForwardingOrdersApiForwardingOrdersGetApiResponse,
        ListForwardingOrdersApiForwardingOrdersGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/forwarding-orders`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            status: queryArg.status,
            entry_status: queryArg.entryStatus,
            date_from: queryArg.dateFrom,
            date_to: queryArg.dateTo,
            weight_from: queryArg.weightFrom,
            weight_to: queryArg.weightTo,
            client_rate_from: queryArg.clientRateFrom,
            client_rate_to: queryArg.clientRateTo,
            transport_rate_from: queryArg.transportRateFrom,
            transport_rate_to: queryArg.transportRateTo,
            profit_from: queryArg.profitFrom,
            profit_to: queryArg.profitTo,
            driver_id: queryArg.driverId,
            driver_manager_id: queryArg.driverManagerId,
            truck_id: queryArg.truckId,
            trailer_id: queryArg.trailerId,
            client_id: queryArg.clientId,
            product_id: queryArg.productId,
            transporter_id: queryArg.transporterId,
            unloading_city: queryArg.unloadingCity,
            unloading_country: queryArg.unloadingCountry,
            loading_city: queryArg.loadingCity,
            loading_country: queryArg.loadingCountry,
            is_national: queryArg.isNational,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["forwarding_orders"],
      }),
      createForwardingOrderApiForwardingOrdersPost: build.mutation<
        CreateForwardingOrderApiForwardingOrdersPostApiResponse,
        CreateForwardingOrderApiForwardingOrdersPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/forwarding-orders`,
          method: "POST",
          body: queryArg.forwardingOrderCreate,
        }),
        invalidatesTags: ["forwarding_orders", "deliveries", "history"],
      }),
      getForwardingOrderApiForwardingOrdersForwardingOrderIdGet: build.query<
        GetForwardingOrderApiForwardingOrdersForwardingOrderIdGetApiResponse,
        GetForwardingOrderApiForwardingOrdersForwardingOrderIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/forwarding-orders/${queryArg.forwardingOrderId}`,
        }),
        providesTags: ["forwarding_orders"],
      }),
      updateForwardingOrderApiForwardingOrdersForwardingOrderIdPut:
        build.mutation<
          UpdateForwardingOrderApiForwardingOrdersForwardingOrderIdPutApiResponse,
          UpdateForwardingOrderApiForwardingOrdersForwardingOrderIdPutApiArg
        >({
          query: (queryArg) => ({
            url: `/api/forwarding-orders/${queryArg.forwardingOrderId}`,
            method: "PUT",
            body: queryArg.forwardingOrderUpdate,
          }),
          invalidatesTags: ["forwarding_orders", "deliveries", "history"],
        }),
      deleteForwardingOrderApiForwardingOrdersForwardingOrderIdDelete:
        build.mutation<
          DeleteForwardingOrderApiForwardingOrdersForwardingOrderIdDeleteApiResponse,
          DeleteForwardingOrderApiForwardingOrdersForwardingOrderIdDeleteApiArg
        >({
          query: (queryArg) => ({
            url: `/api/forwarding-orders/${queryArg.forwardingOrderId}`,
            method: "DELETE",
          }),
          invalidatesTags: ["forwarding_orders", "deliveries", "history"],
        }),
      getWaybillerStatusApiWaybillerStatusGet: build.query<
        GetWaybillerStatusApiWaybillerStatusGetApiResponse,
        GetWaybillerStatusApiWaybillerStatusGetApiArg
      >({
        query: () => ({ url: `/api/waybiller/status` }),
        providesTags: ["waybiller"],
      }),
      listLocationsApiLocationsGet: build.query<
        ListLocationsApiLocationsGetApiResponse,
        ListLocationsApiLocationsGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            country: queryArg.country,
            city: queryArg.city,
            address: queryArg.address,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["locations"],
      }),
      createLocationApiLocationsPost: build.mutation<
        CreateLocationApiLocationsPostApiResponse,
        CreateLocationApiLocationsPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations`,
          method: "POST",
          body: queryArg.locationCreate,
        }),
        invalidatesTags: ["locations", "history"],
      }),
      listCitiesApiLocationsCitiesGet: build.query<
        ListCitiesApiLocationsCitiesGetApiResponse,
        ListCitiesApiLocationsCitiesGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations/cities`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["locations"],
      }),
      getLocationApiLocationsLocationIdGet: build.query<
        GetLocationApiLocationsLocationIdGetApiResponse,
        GetLocationApiLocationsLocationIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/locations/${queryArg.locationId}` }),
        providesTags: ["locations"],
      }),
      updateLocationApiLocationsLocationIdPut: build.mutation<
        UpdateLocationApiLocationsLocationIdPutApiResponse,
        UpdateLocationApiLocationsLocationIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations/${queryArg.locationId}`,
          method: "PUT",
          body: queryArg.locationUpdate,
        }),
        invalidatesTags: ["locations", "history"],
      }),
      deleteLocationApiLocationsLocationIdDelete: build.mutation<
        DeleteLocationApiLocationsLocationIdDeleteApiResponse,
        DeleteLocationApiLocationsLocationIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations/${queryArg.locationId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["locations", "history"],
      }),
      listContractsApiContractsGet: build.query<
        ListContractsApiContractsGetApiResponse,
        ListContractsApiContractsGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            contract_type: queryArg.contractType,
            entry_status: queryArg.entryStatus,
            date_from: queryArg.dateFrom,
            date_to: queryArg.dateTo,
            start_date_from: queryArg.startDateFrom,
            start_date_to: queryArg.startDateTo,
            end_date_from: queryArg.endDateFrom,
            end_date_to: queryArg.endDateTo,
            signed: queryArg.signed,
            price_from: queryArg.priceFrom,
            price_to: queryArg.priceTo,
            weight_from: queryArg.weightFrom,
            weight_to: queryArg.weightTo,
            payment_deadline_days_from: queryArg.paymentDeadlineDaysFrom,
            payment_deadline_days_to: queryArg.paymentDeadlineDaysTo,
            client_id: queryArg.clientId,
            product_id: queryArg.productId,
            location_id: queryArg.locationId,
            city: queryArg.city,
            country: queryArg.country,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["contracts"],
      }),
      createContractApiContractsPost: build.mutation<
        CreateContractApiContractsPostApiResponse,
        CreateContractApiContractsPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts`,
          method: "POST",
          body: queryArg.contractCreate,
        }),
        invalidatesTags: ["contracts", "history"],
      }),
      getContractApiContractsContractIdGet: build.query<
        GetContractApiContractsContractIdGetApiResponse,
        GetContractApiContractsContractIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/contracts/${queryArg.contractId}` }),
        providesTags: ["contracts"],
      }),
      updateContractApiContractsContractIdPut: build.mutation<
        UpdateContractApiContractsContractIdPutApiResponse,
        UpdateContractApiContractsContractIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}`,
          method: "PUT",
          body: queryArg.contractUpdate,
        }),
        invalidatesTags: ["contracts", "transport_orders", "history"],
      }),
      deleteContractApiContractsContractIdDelete: build.mutation<
        DeleteContractApiContractsContractIdDeleteApiResponse,
        DeleteContractApiContractsContractIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["contracts", "transport_orders", "history"],
      }),
      downloadContractTemplateApiContractsContractIdDownloadTemplateGet:
        build.query<
          DownloadContractTemplateApiContractsContractIdDownloadTemplateGetApiResponse,
          DownloadContractTemplateApiContractsContractIdDownloadTemplateGetApiArg
        >({
          query: (queryArg) => ({
            url: `/api/contracts/${queryArg.contractId}/download_template`,
            params: {
              replace_existing_file: queryArg.replaceExistingFile,
              replace_if_empty_file: queryArg.replaceIfEmptyFile,
            },
          }),
          providesTags: ["contracts"],
        }),
      downloadContractFileApiContractsContractIdDownloadGet: build.query<
        DownloadContractFileApiContractsContractIdDownloadGetApiResponse,
        DownloadContractFileApiContractsContractIdDownloadGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/download`,
        }),
        providesTags: ["contracts"],
      }),
      uploadContractFileApiContractsContractIdUploadPost: build.mutation<
        UploadContractFileApiContractsContractIdUploadPostApiResponse,
        UploadContractFileApiContractsContractIdUploadPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/upload`,
          method: "POST",
          body: queryArg.bodyUploadContractFileApiContractsContractIdUploadPost,
        }),
        invalidatesTags: ["contracts"],
      }),
      deleteContractFileApiContractsContractIdUploadDelete: build.mutation<
        DeleteContractFileApiContractsContractIdUploadDeleteApiResponse,
        DeleteContractFileApiContractsContractIdUploadDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/upload`,
          method: "DELETE",
        }),
        invalidatesTags: ["contracts"],
      }),
      listContractTemplatesApiContractTemplatesGet: build.query<
        ListContractTemplatesApiContractTemplatesGetApiResponse,
        ListContractTemplatesApiContractTemplatesGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-templates`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            name: queryArg.name,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["contract-templates"],
      }),
      createContractTemplateApiContractTemplatesPost: build.mutation<
        CreateContractTemplateApiContractTemplatesPostApiResponse,
        CreateContractTemplateApiContractTemplatesPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-templates`,
          method: "POST",
          body: queryArg.contractTemplateCreate,
        }),
        invalidatesTags: ["contract-templates"],
      }),
      getContractTemplateApiContractTemplatesTemplateIdGet: build.query<
        GetContractTemplateApiContractTemplatesTemplateIdGetApiResponse,
        GetContractTemplateApiContractTemplatesTemplateIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-templates/${queryArg.templateId}`,
        }),
        providesTags: ["contract-templates"],
      }),
      updateContractTemplateApiContractTemplatesTemplateIdPut: build.mutation<
        UpdateContractTemplateApiContractTemplatesTemplateIdPutApiResponse,
        UpdateContractTemplateApiContractTemplatesTemplateIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-templates/${queryArg.templateId}`,
          method: "PUT",
          body: queryArg.contractTemplateUpdate,
        }),
        invalidatesTags: ["contract-templates"],
      }),
      deleteContractTemplateApiContractTemplatesTemplateIdDelete:
        build.mutation<
          DeleteContractTemplateApiContractTemplatesTemplateIdDeleteApiResponse,
          DeleteContractTemplateApiContractTemplatesTemplateIdDeleteApiArg
        >({
          query: (queryArg) => ({
            url: `/api/contract-templates/${queryArg.templateId}`,
            method: "DELETE",
          }),
          invalidatesTags: ["contract-templates"],
        }),
      uploadContractTemplateFileApiContractTemplatesTemplateIdUploadPost:
        build.mutation<
          UploadContractTemplateFileApiContractTemplatesTemplateIdUploadPostApiResponse,
          UploadContractTemplateFileApiContractTemplatesTemplateIdUploadPostApiArg
        >({
          query: (queryArg) => ({
            url: `/api/contract-templates/${queryArg.templateId}/upload`,
            method: "POST",
            body: queryArg.bodyUploadContractTemplateFileApiContractTemplatesTemplateIdUploadPost,
          }),
          invalidatesTags: ["contract-templates"],
        }),
      downloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGet:
        build.query<
          DownloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGetApiResponse,
          DownloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGetApiArg
        >({
          query: (queryArg) => ({
            url: `/api/contract-templates/${queryArg.templateId}/download`,
          }),
          providesTags: ["contract-templates"],
        }),
      listHistoryEntriesApiHistoryGet: build.query<
        ListHistoryEntriesApiHistoryGetApiResponse,
        ListHistoryEntriesApiHistoryGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/history`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ids: queryArg.ids,
            parent_id: queryArg.parentId,
            table: queryArg.table,
            field: queryArg.field,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["history"],
      }),
      getHistoryEntryApiHistoryHistoryEntryIdGet: build.query<
        GetHistoryEntryApiHistoryHistoryEntryIdGetApiResponse,
        GetHistoryEntryApiHistoryHistoryEntryIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/history/${queryArg.historyEntryId}`,
        }),
        providesTags: ["history"],
      }),
      listDeliveriesApiDeliveriesGet: build.query<
        ListDeliveriesApiDeliveriesGetApiResponse,
        ListDeliveriesApiDeliveriesGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliveries`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            status: queryArg.status,
            entry_status: queryArg.entryStatus,
            date_from: queryArg.dateFrom,
            date_to: queryArg.dateTo,
            transport_rate_from: queryArg.transportRateFrom,
            transport_rate_to: queryArg.transportRateTo,
            driver_id: queryArg.driverId,
            driver_manager_id: queryArg.driverManagerId,
            unloading_city: queryArg.unloadingCity,
            unloading_country: queryArg.unloadingCountry,
            loading_city: queryArg.loadingCity,
            loading_country: queryArg.loadingCountry,
            is_national: queryArg.isNational,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["deliveries"],
      }),
      getDeliveryApiDeliveriesSourceDeliveryIdGet: build.query<
        GetDeliveryApiDeliveriesSourceDeliveryIdGetApiResponse,
        GetDeliveryApiDeliveriesSourceDeliveryIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliveries/${queryArg.source}/${queryArg.deliveryId}`,
        }),
        providesTags: ["deliveries"],
      }),
      updateDeliveryApiDeliveriesSourceDeliveryIdPut: build.mutation<
        UpdateDeliveryApiDeliveriesSourceDeliveryIdPutApiResponse,
        UpdateDeliveryApiDeliveriesSourceDeliveryIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliveries/${queryArg.source}/${queryArg.deliveryId}`,
          method: "PUT",
          body: queryArg.deliveryUpdate,
        }),
        invalidatesTags: ["deliveries", "history"],
      }),
      listCommentsApiCommentsGet: build.query<
        ListCommentsApiCommentsGetApiResponse,
        ListCommentsApiCommentsGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/comments`,
          params: {
            search: queryArg.search,
            search_fields: queryArg.searchFields,
            limit: queryArg.limit,
            offset: queryArg.offset,
            parent_id: queryArg.parentId,
            table: queryArg.table,
            created_by_id: queryArg.createdById,
            order_by: queryArg.orderBy,
          },
        }),
        providesTags: ["comments"],
      }),
      createCommentApiCommentsPost: build.mutation<
        CreateCommentApiCommentsPostApiResponse,
        CreateCommentApiCommentsPostApiArg
      >({
        query: (queryArg) => ({
          url: `/api/comments`,
          method: "POST",
          body: queryArg.commentCreate,
        }),
        invalidatesTags: invalidatesCommentTags,
      }),
      getCommentApiCommentsCommentIdGet: build.query<
        GetCommentApiCommentsCommentIdGetApiResponse,
        GetCommentApiCommentsCommentIdGetApiArg
      >({
        query: (queryArg) => ({ url: `/api/comments/${queryArg.commentId}` }),
        providesTags: ["comments"],
      }),
      updateCommentApiCommentsCommentIdPut: build.mutation<
        UpdateCommentApiCommentsCommentIdPutApiResponse,
        UpdateCommentApiCommentsCommentIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/api/comments/${queryArg.commentId}`,
          method: "PUT",
          body: queryArg.commentUpdate,
        }),
        invalidatesTags: ["comments"],
      }),
      deleteCommentApiCommentsCommentIdDelete: build.mutation<
        DeleteCommentApiCommentsCommentIdDeleteApiResponse,
        DeleteCommentApiCommentsCommentIdDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/comments/${queryArg.commentId}`,
          method: "DELETE",
        }),
        invalidatesTags: commentAllPossibleTags,
      }),
      rootGet: build.query<RootGetApiResponse, RootGetApiArg>({
        query: () => ({ url: `/` }),
      }),
      healthHealthGet: build.query<
        HealthHealthGetApiResponse,
        HealthHealthGetApiArg
      >({
        query: () => ({ url: `/health` }),
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as api };
export type LoginForAccessTokenApiTokenPostApiResponse =
  /** status 200 Successful Response */ Token;
export type LoginForAccessTokenApiTokenPostApiArg = {
  tokenRequest: TokenRequest;
};
export type LoginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPostApiResponse =
  /** status 200 Successful Response */ Token;
export type LoginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPostApiArg =
  {
    organizationId: number;
  };
export type GetResourceTokenApiResourceTokenResourceResourceIdPostApiResponse =
  /** status 200 Successful Response */ Token;
export type GetResourceTokenApiResourceTokenResourceResourceIdPostApiArg = {
  resource: "ContractTemplate" | "Contract";
  resourceId: string;
};
export type GetCurrentUserInfoApiMeGetApiResponse =
  /** status 200 Successful Response */ UserWithOrganization;
export type GetCurrentUserInfoApiMeGetApiArg = void;
export type GetUsersApiUsersGetApiResponse =
  /** status 200 Successful Response */ User[];
export type GetUsersApiUsersGetApiArg = void;
export type GetOrganizationalUsersApiOrganizationalUsersGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseOrganizationalUser;
export type GetOrganizationalUsersApiOrganizationalUsersGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: email. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  email?: string;
  isActive?: boolean;
  isManager?: boolean;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: email */
  orderBy?: string;
};
export type ListDriversApiDriversGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseUnionDriverDriverDetail;
export type ListDriversApiDriversGetApiArg = {
  /** Include transport and forwarding orders in the response */
  includeOrders?: boolean;
  search?: string;
  /** Comma separated list of fields to search in. Available fields: company, email, first_name, last_name, manager_email, personal_code, phone. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  personalCode?: string;
  managerId?: number;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: company, created_at, email, first_name, last_name, manager_email, personal_code, phone, updated_at */
  orderBy?: string;
};
export type CreateDriverApiDriversPostApiResponse =
  /** status 201 Successful Response */ Driver;
export type CreateDriverApiDriversPostApiArg = {
  driverCreate: DriverCreate;
};
export type GetDriverApiDriversDriverIdGetApiResponse =
  /** status 200 Successful Response */ DriverDetail;
export type GetDriverApiDriversDriverIdGetApiArg = {
  driverId: number;
};
export type UpdateDriverApiDriversDriverIdPutApiResponse =
  /** status 200 Successful Response */ Driver;
export type UpdateDriverApiDriversDriverIdPutApiArg = {
  driverId: number;
  driverUpdate: DriverUpdate;
};
export type DeleteDriverApiDriversDriverIdDeleteApiResponse =
  /** status 200 Successful Response */ Driver;
export type DeleteDriverApiDriversDriverIdDeleteApiArg = {
  driverId: number;
};
export type ListCompaniesApiCompaniesGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseCompany;
export type ListCompaniesApiCompaniesGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: legal_address, name, registry_code. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  name?: string;
  registryCode?: string;
  isSupplier?: boolean;
  isTransporter?: boolean;
  isBuyer?: boolean;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: name, registry_code */
  orderBy?: string;
};
export type CreateCompanyApiCompaniesPostApiResponse =
  /** status 201 Successful Response */ Company;
export type CreateCompanyApiCompaniesPostApiArg = {
  companyCreate: CompanyCreate;
};
export type GetCompanyApiCompaniesCompanyIdGetApiResponse =
  /** status 200 Successful Response */ Company;
export type GetCompanyApiCompaniesCompanyIdGetApiArg = {
  companyId: number;
};
export type UpdateCompanyApiCompaniesCompanyIdPutApiResponse =
  /** status 200 Successful Response */ Company;
export type UpdateCompanyApiCompaniesCompanyIdPutApiArg = {
  companyId: number;
  companyUpdate: CompanyUpdate;
};
export type DeleteCompanyApiCompaniesCompanyIdDeleteApiResponse =
  /** status 200 Successful Response */ Company;
export type DeleteCompanyApiCompaniesCompanyIdDeleteApiArg = {
  companyId: number;
};
export type ListProductsApiProductsGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseProduct;
export type ListProductsApiProductsGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: name. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  name?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `UUID` */
  salesContractTemplateId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `UUID` */
  purchaseContractTemplateId?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: created_at, name, updated_at */
  orderBy?: string;
};
export type CreateProductApiProductsPostApiResponse =
  /** status 201 Successful Response */ Product;
export type CreateProductApiProductsPostApiArg = {
  productCreate: ProductCreate;
};
export type GetProductApiProductsProductIdGetApiResponse =
  /** status 200 Successful Response */ Product;
export type GetProductApiProductsProductIdGetApiArg = {
  productId: number;
};
export type UpdateProductApiProductsProductIdPutApiResponse =
  /** status 200 Successful Response */ Product;
export type UpdateProductApiProductsProductIdPutApiArg = {
  productId: number;
  productUpdate: ProductUpdate;
};
export type DeleteProductApiProductsProductIdDeleteApiResponse =
  /** status 200 Successful Response */ Product;
export type DeleteProductApiProductsProductIdDeleteApiArg = {
  productId: number;
};
export type ListVehiclesApiVehiclesGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseVehicle;
export type ListVehiclesApiVehiclesGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: number, type. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  type?: string;
  number?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: created_at, number, type, updated_at */
  orderBy?: string;
};
export type CreateVehicleApiVehiclesPostApiResponse =
  /** status 201 Successful Response */ Vehicle;
export type CreateVehicleApiVehiclesPostApiArg = {
  vehicleCreate: VehicleCreate;
};
export type GetVehicleApiVehiclesVehicleIdGetApiResponse =
  /** status 200 Successful Response */ Vehicle;
export type GetVehicleApiVehiclesVehicleIdGetApiArg = {
  vehicleId: number;
};
export type UpdateVehicleApiVehiclesVehicleIdPutApiResponse =
  /** status 200 Successful Response */ Vehicle;
export type UpdateVehicleApiVehiclesVehicleIdPutApiArg = {
  vehicleId: number;
  vehicleUpdate: VehicleUpdate;
};
export type DeleteVehicleApiVehiclesVehicleIdDeleteApiResponse =
  /** status 200 Successful Response */ Vehicle;
export type DeleteVehicleApiVehiclesVehicleIdDeleteApiArg = {
  vehicleId: number;
};
export type ListTransportOrdersApiTransportOrdersGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseTransportOrder;
export type ListTransportOrdersApiTransportOrdersGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: buyer_name, buyer_registry_code, driver_manager_email, driver_name, loader_registry_code, loading_address, loading_city, loading_country, number, product_name, purchase_contract_number, purchase_invoice, sales_contract_number, sales_invoice, supplier_name, supplier_registry_code, trailer_number, transporter_name, transporter_registry_code, truck_number, unloading_address, unloading_city, unloading_country. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  status?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  entryStatus?: string;
  loadingDateFrom?: string;
  loadingDateTo?: string;
  unloadingDateFrom?: string;
  unloadingDateTo?: string;
  loadedWeightFrom?: number | string;
  loadedWeightTo?: number | string;
  unloadedWeightFrom?: number | string;
  unloadedWeightTo?: number | string;
  purchasePriceFrom?: number | string;
  purchasePriceTo?: number | string;
  salesPriceFrom?: number | string;
  salesPriceTo?: number | string;
  transportRateFrom?: number | string;
  transportRateTo?: number | string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  productId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverManagerId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  truckId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  trailerId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  supplierId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  buyerId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  transporterId?: string;
  unloadingCity?: string;
  unloadingCountry?: string;
  loadingCity?: string;
  loadingCountry?: string;
  isNational?: boolean;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: buyer_name, created_at, driver_manager_email, driver_name, entry_status, loaded_weight, loader_name, loading_address, loading_city, loading_country, loading_date, number, product_name, purchase_contract_number, purchase_deductions, purchase_invoice, purchase_price, sales_contract_number, sales_deductions, sales_invoice, sales_price, status, supplier_name, trailer_number, transport_rate, transporter_name, truck_number, unloaded_weight, unloading_address, unloading_city, unloading_country, unloading_date, updated_at */
  orderBy?: string;
};
export type CreateTransportOrderApiTransportOrdersPostApiResponse =
  /** status 201 Successful Response */ TransportOrder | TransportOrder[];
export type CreateTransportOrderApiTransportOrdersPostApiArg = {
  transportOrderIn:
    | TransportOrderCreate
    | TransportOrderCreateWithSalesContract;
};
export type GetTransportOrderApiTransportOrdersTransportOrderIdGetApiResponse =
  /** status 200 Successful Response */ TransportOrder;
export type GetTransportOrderApiTransportOrdersTransportOrderIdGetApiArg = {
  transportOrderId: number;
};
export type UpdateTransportOrderApiTransportOrdersTransportOrderIdPutApiResponse =
  /** status 200 Successful Response */ TransportOrder;
export type UpdateTransportOrderApiTransportOrdersTransportOrderIdPutApiArg = {
  transportOrderId: number;
  transportOrderUpdate: TransportOrderUpdate;
};
export type DeleteTransportOrderApiTransportOrdersTransportOrderIdDeleteApiResponse =
  /** status 200 Successful Response */ TransportOrder;
export type DeleteTransportOrderApiTransportOrdersTransportOrderIdDeleteApiArg =
  {
    transportOrderId: number;
  };
export type ListForwardingOrdersApiForwardingOrdersGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseForwardingOrder;
export type ListForwardingOrdersApiForwardingOrdersGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: client_name, client_registry_code, driver_manager_email, driver_name, loading_address, loading_city, loading_country, manager, number, purchase_invoice, purchase_order_number, sales_order_number, trailer_number, transporter_name, transporter_registry_code, truck_number, unloading_address, unloading_city, unloading_country. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  status?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  entryStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  weightFrom?: number | string;
  weightTo?: number | string;
  clientRateFrom?: number | string;
  clientRateTo?: number | string;
  transportRateFrom?: number | string;
  transportRateTo?: number | string;
  profitFrom?: number | string;
  profitTo?: number | string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverManagerId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  truckId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  trailerId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  clientId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  productId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  transporterId?: string;
  unloadingCity?: string;
  unloadingCountry?: string;
  loadingCity?: string;
  loadingCountry?: string;
  isNational?: boolean;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: client_name, client_rate, created_at, date, driver_manager_email, driver_name, entry_status, loading_address, loading_city, loading_country, manager, number, profit, purchase_invoice, purchase_order_number, sales_invoice, sales_order_number, status, trailer_number, transport_rate, transporter_name, truck_number, unloading_address, unloading_city, unloading_country, updated_at, weight */
  orderBy?: string;
};
export type CreateForwardingOrderApiForwardingOrdersPostApiResponse =
  /** status 201 Successful Response */ ForwardingOrder;
export type CreateForwardingOrderApiForwardingOrdersPostApiArg = {
  forwardingOrderCreate: ForwardingOrderCreate;
};
export type GetForwardingOrderApiForwardingOrdersForwardingOrderIdGetApiResponse =
  /** status 200 Successful Response */ ForwardingOrder;
export type GetForwardingOrderApiForwardingOrdersForwardingOrderIdGetApiArg = {
  forwardingOrderId: number;
};
export type UpdateForwardingOrderApiForwardingOrdersForwardingOrderIdPutApiResponse =
  /** status 200 Successful Response */ ForwardingOrder;
export type UpdateForwardingOrderApiForwardingOrdersForwardingOrderIdPutApiArg =
  {
    forwardingOrderId: number;
    forwardingOrderUpdate: ForwardingOrderUpdate;
  };
export type DeleteForwardingOrderApiForwardingOrdersForwardingOrderIdDeleteApiResponse =
  /** status 200 Successful Response */ ForwardingOrder;
export type DeleteForwardingOrderApiForwardingOrdersForwardingOrderIdDeleteApiArg =
  {
    forwardingOrderId: number;
  };
export type GetWaybillerStatusApiWaybillerStatusGetApiResponse =
  /** status 200 Successful Response */ WaybillerIntegrationStatus;
export type GetWaybillerStatusApiWaybillerStatusGetApiArg = void;
export type ListLocationsApiLocationsGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseLocation;
export type ListLocationsApiLocationsGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: address, city, country. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  country?: string;
  city?: string;
  address?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: address, city, country, created_at, updated_at */
  orderBy?: string;
};
export type CreateLocationApiLocationsPostApiResponse =
  /** status 201 Successful Response */ Location;
export type CreateLocationApiLocationsPostApiArg = {
  locationCreate: LocationCreate;
};
export type ListCitiesApiLocationsCitiesGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseCityCountryPair;
export type ListCitiesApiLocationsCitiesGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: . If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: city, country */
  orderBy?: string;
};
export type GetLocationApiLocationsLocationIdGetApiResponse =
  /** status 200 Successful Response */ Location;
export type GetLocationApiLocationsLocationIdGetApiArg = {
  locationId: number;
};
export type UpdateLocationApiLocationsLocationIdPutApiResponse =
  /** status 200 Successful Response */ Location;
export type UpdateLocationApiLocationsLocationIdPutApiArg = {
  locationId: number;
  locationUpdate: LocationUpdate;
};
export type DeleteLocationApiLocationsLocationIdDeleteApiResponse =
  /** status 200 Successful Response */ Location;
export type DeleteLocationApiLocationsLocationIdDeleteApiArg = {
  locationId: number;
};
export type ListContractsApiContractsGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseContract;
export type ListContractsApiContractsGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: address, broker, broker_invoice, city, client_name, client_registry_code, contract_number, contract_type, country, location_address, product_name. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  contractType?: "sales" | "purchase";
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  entryStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  signed?: boolean;
  priceFrom?: number | string;
  priceTo?: number | string;
  weightFrom?: number | string;
  weightTo?: number | string;
  paymentDeadlineDaysFrom?: number;
  paymentDeadlineDaysTo?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  clientId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  productId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  locationId?: string;
  city?: string;
  country?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: address, broker, broker_invoice, city, client_name, contract_number, contract_type, country, created_at, date, delivered_weight, end_date, entry_status, estimated_weight, missing_weight, payment_deadline_days, price, product_name, remaining_weight, signed, start_date, updated_at, weight */
  orderBy?: string;
};
export type CreateContractApiContractsPostApiResponse =
  /** status 201 Successful Response */ Contract;
export type CreateContractApiContractsPostApiArg = {
  contractCreate: ContractCreate;
};
export type GetContractApiContractsContractIdGetApiResponse =
  /** status 200 Successful Response */ Contract;
export type GetContractApiContractsContractIdGetApiArg = {
  contractId: number;
};
export type UpdateContractApiContractsContractIdPutApiResponse =
  /** status 200 Successful Response */ Contract;
export type UpdateContractApiContractsContractIdPutApiArg = {
  contractId: number;
  contractUpdate: ContractUpdate;
};
export type DeleteContractApiContractsContractIdDeleteApiResponse =
  /** status 200 Successful Response */ Contract;
export type DeleteContractApiContractsContractIdDeleteApiArg = {
  contractId: number;
};
export type DownloadContractTemplateApiContractsContractIdDownloadTemplateGetApiResponse =
  /** status 200 Successful Response */ any;
export type DownloadContractTemplateApiContractsContractIdDownloadTemplateGetApiArg =
  {
    contractId: number;
    replaceExistingFile?: boolean;
    replaceIfEmptyFile?: boolean;
  };
export type DownloadContractFileApiContractsContractIdDownloadGetApiResponse =
  /** status 200 Successful Response */ any;
export type DownloadContractFileApiContractsContractIdDownloadGetApiArg = {
  contractId: number;
};
export type UploadContractFileApiContractsContractIdUploadPostApiResponse =
  unknown;
export type UploadContractFileApiContractsContractIdUploadPostApiArg = {
  contractId: number;
  bodyUploadContractFileApiContractsContractIdUploadPost: BodyUploadContractFileApiContractsContractIdUploadPost;
};
export type DeleteContractFileApiContractsContractIdUploadDeleteApiResponse =
  unknown;
export type DeleteContractFileApiContractsContractIdUploadDeleteApiArg = {
  contractId: number;
};
export type ListContractTemplatesApiContractTemplatesGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseContractTemplate;
export type ListContractTemplatesApiContractTemplatesGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: name. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `UUID` */
  ids?: string;
  name?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: name */
  orderBy?: string;
};
export type CreateContractTemplateApiContractTemplatesPostApiResponse =
  /** status 201 Successful Response */ ContractTemplate;
export type CreateContractTemplateApiContractTemplatesPostApiArg = {
  contractTemplateCreate: ContractTemplateCreate;
};
export type GetContractTemplateApiContractTemplatesTemplateIdGetApiResponse =
  /** status 200 Successful Response */ ContractTemplate;
export type GetContractTemplateApiContractTemplatesTemplateIdGetApiArg = {
  templateId: string;
};
export type UpdateContractTemplateApiContractTemplatesTemplateIdPutApiResponse =
  /** status 200 Successful Response */ ContractTemplate;
export type UpdateContractTemplateApiContractTemplatesTemplateIdPutApiArg = {
  templateId: string;
  contractTemplateUpdate: ContractTemplateUpdate;
};
export type DeleteContractTemplateApiContractTemplatesTemplateIdDeleteApiResponse =
  /** status 200 Successful Response */ ContractTemplate;
export type DeleteContractTemplateApiContractTemplatesTemplateIdDeleteApiArg = {
  templateId: string;
};
export type UploadContractTemplateFileApiContractTemplatesTemplateIdUploadPostApiResponse =
  unknown;
export type UploadContractTemplateFileApiContractTemplatesTemplateIdUploadPostApiArg =
  {
    templateId: string;
    bodyUploadContractTemplateFileApiContractTemplatesTemplateIdUploadPost: BodyUploadContractTemplateFileApiContractTemplatesTemplateIdUploadPost;
  };
export type DownloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGetApiResponse =
  /** status 200 Successful Response */ any;
export type DownloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGetApiArg =
  {
    templateId: string;
  };
export type ListHistoryEntriesApiHistoryGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseHistoryEntry;
export type ListHistoryEntriesApiHistoryGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: after, before, field, table. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  ids?: string;
  parentId?: number;
  table?:
    | "Company"
    | "Contract"
    | "Driver"
    | "ForwardingOrder"
    | "Location"
    | "Organization"
    | "Product"
    | "TransportOrder"
    | "User"
    | "Vehicle";
  field?:
    | "address"
    | "broker"
    | "broker_invoice"
    | "buyer"
    | "city"
    | "client"
    | "client_rate"
    | "comment_created"
    | "comment_deleted"
    | "comment_edited"
    | "company"
    | "completion_deadline"
    | "contact_person_name"
    | "contact_phone_number"
    | "contract_number"
    | "contract_type"
    | "country"
    | "date"
    | "deleted_at"
    | "driver"
    | "email"
    | "end_date"
    | "entry_status"
    | "file"
    | "file_extension"
    | "first_name"
    | "id"
    | "is_active"
    | "is_buyer"
    | "is_supplier"
    | "is_transporter"
    | "last_name"
    | "latitude"
    | "legal_address"
    | "loaded_weight"
    | "loaded_weight_edited"
    | "loading_date"
    | "loading_location"
    | "location"
    | "longitude"
    | "manager"
    | "marked_fields"
    | "name"
    | "number"
    | "password"
    | "payment_deadline_days"
    | "personal_code"
    | "phone"
    | "price"
    | "product"
    | "profit"
    | "purchase_contract"
    | "purchase_contract_template"
    | "purchase_deductions"
    | "purchase_invoice"
    | "purchase_order_number"
    | "purchase_price"
    | "registry_code"
    | "sales_contract"
    | "sales_contract_template"
    | "sales_deductions"
    | "sales_invoice"
    | "sales_order_number"
    | "sales_price"
    | "signed"
    | "sort_rank"
    | "start_date"
    | "status"
    | "supplier"
    | "trailer"
    | "transport_rate"
    | "transporter"
    | "truck"
    | "type"
    | "unloaded_weight"
    | "unloaded_weight_edited"
    | "unloading_date"
    | "unloading_location"
    | "waybiller_api_key"
    | "waybiller_api_url"
    | "waybiller_data_hash"
    | "waybiller_hidden_destination_raw_id"
    | "waybiller_hide_destinations"
    | "waybiller_id"
    | "waybiller_registry_code"
    | "weight"
    | "weight_edited";
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: created_at, field, parent_id, table */
  orderBy?: string;
};
export type GetHistoryEntryApiHistoryHistoryEntryIdGetApiResponse =
  /** status 200 Successful Response */ HistoryEntry;
export type GetHistoryEntryApiHistoryHistoryEntryIdGetApiArg = {
  /** The ID of the history entry to retrieve */
  historyEntryId: number;
};
export type ListDeliveriesApiDeliveriesGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseDelivery;
export type ListDeliveriesApiDeliveriesGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: driver_manager_email, driver_name, loading_address, loading_city, loading_country, unloading_address, unloading_city, unloading_country. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  status?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `str` */
  entryStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  transportRateFrom?: number | string;
  transportRateTo?: number | string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverId?: string;
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  driverManagerId?: string;
  unloadingCity?: string;
  unloadingCountry?: string;
  loadingCity?: string;
  loadingCountry?: string;
  isNational?: boolean;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: created_at, date, driver_manager_email, driver_name, entry_status, loading_address, loading_city, loading_country, sort_rank, status, transport_rate, unloading_address, unloading_city, unloading_country, updated_at */
  orderBy?: string;
};
export type GetDeliveryApiDeliveriesSourceDeliveryIdGetApiResponse =
  /** status 200 Successful Response */ Delivery;
export type GetDeliveryApiDeliveriesSourceDeliveryIdGetApiArg = {
  source: "TransportOrder" | "ForwardingOrder";
  deliveryId: number;
};
export type UpdateDeliveryApiDeliveriesSourceDeliveryIdPutApiResponse =
  /** status 200 Successful Response */ Delivery;
export type UpdateDeliveryApiDeliveriesSourceDeliveryIdPutApiArg = {
  source: "TransportOrder" | "ForwardingOrder";
  deliveryId: number;
  deliveryUpdate: DeliveryUpdate;
};
export type ListCommentsApiCommentsGetApiResponse =
  /** status 200 Successful Response */ PaginatedResponseComment;
export type ListCommentsApiCommentsGetApiArg = {
  search?: string;
  /** Comma separated list of fields to search in. Available fields: table, text. If empty, all available fields are searched. */
  searchFields?: string;
  limit?: number;
  offset?: number;
  parentId?: number;
  table?:
    | "Company"
    | "Contract"
    | "Driver"
    | "ForwardingOrder"
    | "Location"
    | "Product"
    | "TransportOrder";
  /** Comma-separated list of values to match. Each value must be a valid string representation of `int` */
  createdById?: string;
  /** Comma-separated list of fields to order by. Defaults to ascending order. Use `-` prefix for descending order. Valid fields: created_at, updated_at */
  orderBy?: string;
};
export type CreateCommentApiCommentsPostApiResponse =
  /** status 201 Successful Response */ Comment;
export type CreateCommentApiCommentsPostApiArg = {
  commentCreate: CommentCreate;
};
export type GetCommentApiCommentsCommentIdGetApiResponse =
  /** status 200 Successful Response */ Comment;
export type GetCommentApiCommentsCommentIdGetApiArg = {
  commentId: number;
};
export type UpdateCommentApiCommentsCommentIdPutApiResponse =
  /** status 200 Successful Response */ Comment;
export type UpdateCommentApiCommentsCommentIdPutApiArg = {
  commentId: number;
  commentUpdate: CommentUpdate;
};
export type DeleteCommentApiCommentsCommentIdDeleteApiResponse = unknown;
export type DeleteCommentApiCommentsCommentIdDeleteApiArg = {
  commentId: number;
};
export type RootGetApiResponse = /** status 200 Successful Response */ any;
export type RootGetApiArg = void;
export type HealthHealthGetApiResponse =
  /** status 200 Successful Response */ any;
export type HealthHealthGetApiArg = void;
export type Token = {
  access_token: string;
  token_type: string;
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type ErrorCode =
  | "unknown"
  | "validation_error"
  | "duplicate"
  | "not_found"
  | "expired_token"
  | "invalid_token"
  | "invalid_user"
  | "invalid_user_organization"
  | "invalid_in_filter"
  | "invalid_search_fields"
  | "invalid_order_by"
  | "invalid_related_company"
  | "invalid_related_driver"
  | "invalid_related_vehicle"
  | "invalid_related_product"
  | "invalid_related_location"
  | "invalid_related_contract"
  | "conflicting_contract_data"
  | "invalid_count"
  | "forwarding_order_requires_date"
  | "related_entity_prevents_deletion"
  | "template_rendering_error";
export type ErrorResponse = {
  detail: string | ValidationError[];
  error_code: ErrorCode;
};
export type TokenRequest = {
  email: string;
  password: string;
};
export type User = {
  email: string;
  is_active: boolean;
  id: number;
  created_at: string;
  updated_at: string | null;
};
export type Organization = {
  name: string;
  id: number;
  created_at: string;
  updated_at: string | null;
};
export type UserWithOrganization = {
  user: User;
  organization: Organization | null;
  is_manager?: boolean | null;
};
export type OrganizationalUser = {
  email: string;
  is_active: boolean;
  id: number;
  created_at: string;
  updated_at: string | null;
  is_manager?: boolean | null;
};
export type PaginatedResponseOrganizationalUser = {
  items: OrganizationalUser[];
  offset: number;
  limit: number;
  total: number;
};
export type CompanyInfo = {
  id: number;
  name: string;
};
export type DriverVehicleInfo = {
  id: number;
  type: "truck" | "trailer";
  number: string;
  default_truck_for_driver: boolean;
  default_trailer_for_driver: boolean;
};
export type Driver = {
  first_name: string;
  last_name: string;
  phone: string;
  personal_code: string;
  email?: string;
  id: number;
  organization_id: number;
  company_id?: number | null;
  company?: CompanyInfo | null;
  manager_id?: number | null;
  manager?: OrganizationalUser | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  vehicles: DriverVehicleInfo[];
  comments_count: number;
};
export type TransportOrderInfo = {
  id: number;
  type: "TransportOrder";
  loading_date?: string | null;
  unloading_date?: string | null;
  supplier?: CompanyInfo | null;
  buyer: CompanyInfo;
  truck_number?: string | null;
  trailer_number?: string | null;
};
export type ForwardingOrderInfo = {
  id: number;
  type: "ForwardingOrder";
  date: string;
  client: CompanyInfo;
  truck_number?: string | null;
  trailer_number?: string | null;
};
export type DriverDetail = {
  first_name: string;
  last_name: string;
  phone: string;
  personal_code: string;
  email?: string;
  id: number;
  organization_id: number;
  company_id?: number | null;
  company?: CompanyInfo | null;
  manager_id?: number | null;
  manager?: OrganizationalUser | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  vehicles: DriverVehicleInfo[];
  comments_count: number;
  deliveries?: (TransportOrderInfo | ForwardingOrderInfo)[];
};
export type PaginatedResponseUnionDriverDriverDetail = {
  items: (Driver | DriverDetail)[];
  offset: number;
  limit: number;
  total: number;
};
export type DriverCreate = {
  first_name: string;
  last_name: string;
  phone?: string;
  personal_code?: string;
  email?: string;
  company_id?: number | null;
  manager_id?: number | null;
  vehicle_ids?: number[] | null;
};
export type DriverUpdate = {
  first_name?: string;
  last_name?: string;
  phone?: string;
  personal_code?: string;
  email?: string;
  company_id?: number | null;
  manager_id?: number | null;
  vehicle_ids?: number[];
  default_truck_id?: number;
  default_trailer_id?: number;
  default_vehicle_id?: number | null;
};
export type Company = {
  name: string;
  registry_code: string;
  legal_address: string;
  is_supplier: boolean;
  is_transporter: boolean;
  is_buyer: boolean;
  id: number;
  organization_id: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  comments_count: number;
};
export type PaginatedResponseCompany = {
  items: Company[];
  offset: number;
  limit: number;
  total: number;
};
export type CompanyCreate = {
  name: string;
  registry_code: string;
  legal_address: string;
  is_supplier: boolean;
  is_transporter: boolean;
  is_buyer: boolean;
};
export type CompanyUpdate = {
  name?: string;
  registry_code?: string;
  legal_address?: string;
  is_supplier?: boolean;
  is_transporter?: boolean;
  is_buyer?: boolean;
};
export type ContractTemplateInfo = {
  id: string;
  name: string;
};
export type Product = {
  name: string;
  id: number;
  organization_id: number;
  purchase_contract_template_id: string | null;
  sales_contract_template_id: string | null;
  purchase_contract_template: ContractTemplateInfo | null;
  sales_contract_template: ContractTemplateInfo | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  comments_count: number;
};
export type PaginatedResponseProduct = {
  items: Product[];
  offset: number;
  limit: number;
  total: number;
};
export type ProductCreate = {
  name: string;
  purchase_contract_template_id?: string | null;
  sales_contract_template_id?: string | null;
};
export type ProductUpdate = {
  name?: string;
  purchase_contract_template_id?: string | null;
  sales_contract_template_id?: string | null;
};
export type Vehicle = {
  type: "truck" | "trailer";
  number: string;
  id: number;
  organization_id: number;
  created_at: string;
  updated_at: string | null;
};
export type PaginatedResponseVehicle = {
  items: Vehicle[];
  offset: number;
  limit: number;
  total: number;
};
export type VehicleCreate = {
  type: "truck" | "trailer";
  number: string;
  driver_ids?: number[] | null;
};
export type VehicleUpdate = {
  type?: "truck" | "trailer";
  number?: string;
};
export type ProductInfo = {
  id: number;
  name: string;
};
export type UserInfo = {
  email: string;
};
export type ManagerInfo = {
  id: number;
  user: UserInfo;
};
export type DriverInfo = {
  id: number;
  first_name: string;
  last_name: string;
  manager: ManagerInfo | null;
};
export type VehicleInfo = {
  id: number;
  type: string;
  number: string;
};
export type ContractInfo = {
  id: number;
  contract_number: string;
  contract_type: string;
};
export type LocationInfo = {
  id: number;
  country: string;
  city: string;
  address: string;
};
export type TransportOrder = {
  loading_date: string | null;
  loaded_weight: string | null;
  unloading_date: string | null;
  unloaded_weight: string | null;
  entry_status: "default" | "pending" | "completed" | "deleted";
  purchase_price: string | null;
  purchase_invoice: string;
  purchase_deductions: string;
  sales_price: string | null;
  sales_invoice: string;
  sales_deductions: string;
  transport_rate: string | null;
  marked_fields: string[];
  number: string;
  id: number;
  status:
    | "DRAFT"
    | "NEW"
    | "APPROACHING_ORIGIN"
    | "APPROACHING_DESTINATION"
    | "IN_DESTINATION"
    | "CONFIRMED"
    | "CANCELLED";
  product: ProductInfo | null;
  driver: DriverInfo | null;
  truck: VehicleInfo | null;
  trailer: VehicleInfo | null;
  supplier: CompanyInfo | null;
  buyer: CompanyInfo;
  transporter: CompanyInfo | null;
  purchase_contract: ContractInfo | null;
  sales_contract: ContractInfo | null;
  loading_location: LocationInfo | null;
  unloading_location: LocationInfo;
  ready_for_waybiller: boolean;
  fields_not_ready_for_waybiller: {
    [key: string]: (("string_too_short" | "string_too_long") | string)[];
  };
  created_at: string;
  updated_at: string | null;
  comments_count: number;
};
export type PaginatedResponseTransportOrder = {
  items: TransportOrder[];
  offset: number;
  limit: number;
  total: number;
};
export type TransportOrderCreate = {
  loading_date?: string | null;
  loaded_weight?: number | string | null;
  unloading_date?: string | null;
  unloaded_weight?: number | string | null;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  purchase_price?: number | string | null;
  purchase_invoice?: string;
  purchase_deductions?: string;
  sales_price?: number | string | null;
  sales_invoice?: string;
  sales_deductions?: string;
  transport_rate?: number | string | null;
  marked_fields?: string[];
  product_id?: number | null;
  driver_id?: number | null;
  truck_id?: number | null;
  trailer_id?: number | null;
  supplier_id?: number | null;
  buyer_id: number;
  transporter_id?: number | null;
  purchase_contract_id?: number | null;
  sales_contract_id?: null;
  loading_location_id?: number | null;
  unloading_location_id: number;
  count?: ("from_sales_contract" | "from_purchase_contract") | number | null;
};
export type TransportOrderCreateWithSalesContract = {
  loading_date?: string | null;
  loaded_weight?: number | string | null;
  unloading_date?: string | null;
  unloaded_weight?: number | string | null;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  purchase_price?: number | string | null;
  purchase_invoice?: string;
  purchase_deductions?: string;
  sales_price?: number | string | null;
  sales_invoice?: string;
  sales_deductions?: string;
  transport_rate?: number | string | null;
  marked_fields?: string[];
  product_id?: number | null;
  driver_id?: number | null;
  truck_id?: number | null;
  trailer_id?: number | null;
  supplier_id?: number | null;
  buyer_id?: number | null;
  transporter_id?: number | null;
  purchase_contract_id?: number | null;
  sales_contract_id: number;
  loading_location_id?: number | null;
  unloading_location_id?: number | null;
  count?: ("from_sales_contract" | "from_purchase_contract") | number | null;
};
export type TransportOrderUpdate = {
  loading_date?: string | null;
  loaded_weight?: number | string | null;
  unloading_date?: string | null;
  unloaded_weight?: number | string | null;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  purchase_price?: number | string | null;
  purchase_invoice?: string;
  purchase_deductions?: string;
  sales_price?: number | string | null;
  sales_invoice?: string;
  sales_deductions?: string | null;
  transport_rate?: number | string | null;
  marked_fields?: string[];
  product_id?: number | null;
  driver_id?: number | null;
  truck_id?: number | null;
  trailer_id?: number | null;
  supplier_id?: number | null;
  loading_location_id?: number | null;
  purchase_contract_id?: number | null;
  buyer_id?: number;
  unloading_location_id?: number;
  sales_contract_id?: number | null;
  transporter_id?: number | null;
};
export type ForwardingOrder = {
  date: string;
  client_rate: string;
  sales_invoice: string;
  weight: string | null;
  transport_rate: string | null;
  purchase_invoice: string;
  purchase_order_number: string;
  sales_order_number: string;
  entry_status: "default" | "pending" | "completed" | "deleted";
  marked_fields: string[];
  id: number;
  organization_id: number;
  number: string;
  status:
    | "DRAFT"
    | "NEW"
    | "APPROACHING_ORIGIN"
    | "APPROACHING_DESTINATION"
    | "IN_DESTINATION"
    | "CONFIRMED"
    | "CANCELLED";
  client: CompanyInfo;
  product: ProductInfo;
  transporter: CompanyInfo | null;
  driver: DriverInfo | null;
  truck: VehicleInfo | null;
  trailer: VehicleInfo | null;
  loading_location: LocationInfo;
  unloading_location: LocationInfo | null;
  ready_for_waybiller: boolean;
  fields_not_ready_for_waybiller: {
    [key: string]: (("string_too_short" | "string_too_long") | string)[];
  };
  profit: string | null;
  manager: UserInfo | null;
  created_at: string;
  updated_at: string | null;
  comments_count: number;
};
export type PaginatedResponseForwardingOrder = {
  items: ForwardingOrder[];
  offset: number;
  limit: number;
  total: number;
};
export type ForwardingOrderCreate = {
  date: string;
  client_rate: number | string;
  sales_invoice?: string;
  weight?: number | string | null;
  transport_rate?: number | string | null;
  purchase_invoice?: string;
  purchase_order_number?: string;
  sales_order_number?: string;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  marked_fields?: string[];
  client_id: number;
  product_id: number;
  loading_location_id: number;
  transporter_id?: number | null;
  unloading_location_id?: number | null;
  driver_id?: number | null;
  truck_id?: number | null;
  trailer_id?: number | null;
};
export type ForwardingOrderUpdate = {
  date?: string;
  client_rate?: number | string;
  sales_invoice?: string;
  weight?: number | string | null;
  transport_rate?: number | string | null;
  purchase_invoice?: string;
  purchase_order_number?: string;
  sales_order_number?: string;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  marked_fields?: string[];
  client_id?: number;
  product_id?: number;
  transporter_id?: number | null;
  loading_location_id?: number;
  unloading_location_id?: number | null;
  driver_id?: number | null;
  truck_id?: number | null;
  trailer_id?: number | null;
};
export type WaybillerIntegrationStatus = {
  status: "disabled" | "not_configured" | "ready" | "error";
  api_url: string | null;
};
export type Location = {
  country: string;
  city: string;
  address: string;
  contact_phone_number: string;
  contact_person_name: string;
  latitude: number | null;
  longitude: number | null;
  id: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  comments_count: number;
};
export type PaginatedResponseLocation = {
  items: Location[];
  offset: number;
  limit: number;
  total: number;
};
export type LocationCreate = {
  country: string;
  city: string;
  address: string;
  contact_phone_number?: string;
  contact_person_name?: string;
  latitude?: number | null;
  longitude?: number | null;
};
export type CityCountryPair = {
  city: string;
  country: string;
};
export type PaginatedResponseCityCountryPair = {
  items: CityCountryPair[];
  offset: number;
  limit: number;
  total: number;
};
export type LocationUpdate = {
  country?: string;
  city?: string;
  address?: string;
  contact_phone_number?: string;
  contact_person_name?: string;
  latitude?: number | null;
  longitude?: number | null;
};
export type Contract = {
  date: string;
  start_date: string;
  end_date: string;
  signed: boolean;
  contract_number: string;
  payment_deadline_days: number | null;
  price: string;
  weight: string | null;
  broker: string;
  broker_invoice: string;
  entry_status: "default" | "completed" | "deleted";
  marked_fields: string[];
  id: number;
  contract_type: "purchase" | "sales";
  client: CompanyInfo;
  product: ProductInfo;
  location: LocationInfo | null;
  created_at: string;
  updated_at: string | null;
  delivered_weight: string;
  remaining_weight: string;
  estimated_weight: string;
  missing_weight: string;
  has_contract_template: boolean;
  comments_count: number;
  file_exists: boolean;
};
export type PaginatedResponseContract = {
  items: Contract[];
  offset: number;
  limit: number;
  total: number;
};
export type ContractCreate = {
  date: string;
  start_date: string;
  end_date: string;
  signed: boolean;
  contract_number: string;
  payment_deadline_days?: number | null;
  price: number | string;
  weight?: number | string | null;
  broker?: string;
  broker_invoice?: string;
  entry_status?: "default" | "pending" | "completed" | "deleted";
  marked_fields?: string[];
  contract_type: "purchase" | "sales";
  client_id: number;
  product_id: number;
  location_id?: number | null;
};
export type ContractUpdate = {
  date?: string;
  start_date?: string;
  end_date?: string;
  signed?: boolean;
  contract_number?: string;
  client_id?: number;
  payment_deadline_days?: number | null;
  price?: number | string;
  product_id?: number;
  weight?: number | string | null;
  location_id?: number | null;
  broker?: string;
  broker_invoice?: string;
  entry_status?: "default" | "completed" | "deleted";
  marked_fields?: string[];
};
export type BodyUploadContractFileApiContractsContractIdUploadPost = FormData;
export type ContractTemplate = {
  name: string;
  id: string;
  organization_id: number;
  file_exists: boolean;
};
export type PaginatedResponseContractTemplate = {
  items: ContractTemplate[];
  offset: number;
  limit: number;
  total: number;
};
export type ContractTemplateCreate = {
  name: string;
};
export type ContractTemplateUpdate = {
  name?: string;
};
export type BodyUploadContractTemplateFileApiContractTemplatesTemplateIdUploadPost =
  FormData;
export type HistoryEntry = {
  id: number;
  parent_id: number;
  table:
    | "Company"
    | "Contract"
    | "Driver"
    | "ForwardingOrder"
    | "Location"
    | "Organization"
    | "Product"
    | "TransportOrder"
    | "User"
    | "Vehicle";
  field:
    | "address"
    | "broker"
    | "broker_invoice"
    | "buyer"
    | "city"
    | "client"
    | "client_rate"
    | "comment_created"
    | "comment_deleted"
    | "comment_edited"
    | "company"
    | "completion_deadline"
    | "contact_person_name"
    | "contact_phone_number"
    | "contract_number"
    | "contract_type"
    | "country"
    | "date"
    | "deleted_at"
    | "driver"
    | "email"
    | "end_date"
    | "entry_status"
    | "file"
    | "file_extension"
    | "first_name"
    | "id"
    | "is_active"
    | "is_buyer"
    | "is_supplier"
    | "is_transporter"
    | "last_name"
    | "latitude"
    | "legal_address"
    | "loaded_weight"
    | "loaded_weight_edited"
    | "loading_date"
    | "loading_location"
    | "location"
    | "longitude"
    | "manager"
    | "marked_fields"
    | "name"
    | "number"
    | "password"
    | "payment_deadline_days"
    | "personal_code"
    | "phone"
    | "price"
    | "product"
    | "profit"
    | "purchase_contract"
    | "purchase_contract_template"
    | "purchase_deductions"
    | "purchase_invoice"
    | "purchase_order_number"
    | "purchase_price"
    | "registry_code"
    | "sales_contract"
    | "sales_contract_template"
    | "sales_deductions"
    | "sales_invoice"
    | "sales_order_number"
    | "sales_price"
    | "signed"
    | "sort_rank"
    | "start_date"
    | "status"
    | "supplier"
    | "trailer"
    | "transport_rate"
    | "transporter"
    | "truck"
    | "type"
    | "unloaded_weight"
    | "unloaded_weight_edited"
    | "unloading_date"
    | "unloading_location"
    | "waybiller_api_key"
    | "waybiller_api_url"
    | "waybiller_data_hash"
    | "waybiller_hidden_destination_raw_id"
    | "waybiller_hide_destinations"
    | "waybiller_id"
    | "waybiller_registry_code"
    | "weight"
    | "weight_edited";
  before: string | null;
  after: string | null;
  created_at: string;
  created_by_name?: string;
};
export type PaginatedResponseHistoryEntry = {
  items: HistoryEntry[];
  offset: number;
  limit: number;
  total: number;
};
export type Delivery = {
  sort_rank: number;
  date: string | null;
  transport_rate: string | null;
  entry_status: "default" | "pending" | "completed" | "deleted";
  marked_fields: string[];
  id: number;
  source: "TransportOrder" | "ForwardingOrder";
  status:
    | "DRAFT"
    | "NEW"
    | "APPROACHING_ORIGIN"
    | "APPROACHING_DESTINATION"
    | "IN_DESTINATION"
    | "CONFIRMED"
    | "CANCELLED";
  driver: DriverInfo | null;
  loading_location: LocationInfo | null;
  unloading_location: LocationInfo | null;
  created_at: string;
  updated_at: string | null;
  comments_count: number;
};
export type PaginatedResponseDelivery = {
  items: Delivery[];
  offset: number;
  limit: number;
  total: number;
};
export type DeliveryUpdate = {
  sort_rank?: number | null;
  date?: string | null;
  transport_rate?: number | string | null;
  entry_status?: ("default" | "pending" | "completed" | "deleted") | null;
  marked_fields?: string[] | null;
};
export type Comment = {
  text: string;
  id: number;
  parent_id: number;
  table:
    | "Company"
    | "Contract"
    | "Driver"
    | "ForwardingOrder"
    | "Location"
    | "Product"
    | "TransportOrder";
  created_at: string;
  created_by: UserInfo;
  editable?: boolean;
  updated_at?: string | null;
  updated_by?: UserInfo | null;
};
export type PaginatedResponseComment = {
  items: Comment[];
  offset: number;
  limit: number;
  total: number;
};
export type CommentCreate = {
  text: string;
  parent_id: number;
  table:
    | "Company"
    | "Contract"
    | "Driver"
    | "ForwardingOrder"
    | "Location"
    | "Product"
    | "TransportOrder";
};
export type CommentUpdate = {
  text: string;
};
export const {
  useLoginForAccessTokenApiTokenPostMutation,
  useLoginForAccessTokenAsOrganizationApiOrganizationsOrganizationIdTokenPostMutation,
  useGetResourceTokenApiResourceTokenResourceResourceIdPostMutation,
  useGetCurrentUserInfoApiMeGetQuery,
  useGetUsersApiUsersGetQuery,
  useGetOrganizationalUsersApiOrganizationalUsersGetQuery,
  useListDriversApiDriversGetQuery,
  useCreateDriverApiDriversPostMutation,
  useGetDriverApiDriversDriverIdGetQuery,
  useUpdateDriverApiDriversDriverIdPutMutation,
  useDeleteDriverApiDriversDriverIdDeleteMutation,
  useListCompaniesApiCompaniesGetQuery,
  useCreateCompanyApiCompaniesPostMutation,
  useGetCompanyApiCompaniesCompanyIdGetQuery,
  useUpdateCompanyApiCompaniesCompanyIdPutMutation,
  useDeleteCompanyApiCompaniesCompanyIdDeleteMutation,
  useListProductsApiProductsGetQuery,
  useCreateProductApiProductsPostMutation,
  useGetProductApiProductsProductIdGetQuery,
  useUpdateProductApiProductsProductIdPutMutation,
  useDeleteProductApiProductsProductIdDeleteMutation,
  useListVehiclesApiVehiclesGetQuery,
  useCreateVehicleApiVehiclesPostMutation,
  useGetVehicleApiVehiclesVehicleIdGetQuery,
  useUpdateVehicleApiVehiclesVehicleIdPutMutation,
  useDeleteVehicleApiVehiclesVehicleIdDeleteMutation,
  useListTransportOrdersApiTransportOrdersGetQuery,
  useCreateTransportOrderApiTransportOrdersPostMutation,
  useGetTransportOrderApiTransportOrdersTransportOrderIdGetQuery,
  useUpdateTransportOrderApiTransportOrdersTransportOrderIdPutMutation,
  useDeleteTransportOrderApiTransportOrdersTransportOrderIdDeleteMutation,
  useListForwardingOrdersApiForwardingOrdersGetQuery,
  useCreateForwardingOrderApiForwardingOrdersPostMutation,
  useGetForwardingOrderApiForwardingOrdersForwardingOrderIdGetQuery,
  useUpdateForwardingOrderApiForwardingOrdersForwardingOrderIdPutMutation,
  useDeleteForwardingOrderApiForwardingOrdersForwardingOrderIdDeleteMutation,
  useGetWaybillerStatusApiWaybillerStatusGetQuery,
  useListLocationsApiLocationsGetQuery,
  useCreateLocationApiLocationsPostMutation,
  useListCitiesApiLocationsCitiesGetQuery,
  useGetLocationApiLocationsLocationIdGetQuery,
  useUpdateLocationApiLocationsLocationIdPutMutation,
  useDeleteLocationApiLocationsLocationIdDeleteMutation,
  useListContractsApiContractsGetQuery,
  useCreateContractApiContractsPostMutation,
  useGetContractApiContractsContractIdGetQuery,
  useUpdateContractApiContractsContractIdPutMutation,
  useDeleteContractApiContractsContractIdDeleteMutation,
  useDownloadContractTemplateApiContractsContractIdDownloadTemplateGetQuery,
  useDownloadContractFileApiContractsContractIdDownloadGetQuery,
  useUploadContractFileApiContractsContractIdUploadPostMutation,
  useDeleteContractFileApiContractsContractIdUploadDeleteMutation,
  useListContractTemplatesApiContractTemplatesGetQuery,
  useCreateContractTemplateApiContractTemplatesPostMutation,
  useGetContractTemplateApiContractTemplatesTemplateIdGetQuery,
  useUpdateContractTemplateApiContractTemplatesTemplateIdPutMutation,
  useDeleteContractTemplateApiContractTemplatesTemplateIdDeleteMutation,
  useUploadContractTemplateFileApiContractTemplatesTemplateIdUploadPostMutation,
  useDownloadContractTemplateFileApiContractTemplatesTemplateIdDownloadGetQuery,
  useListHistoryEntriesApiHistoryGetQuery,
  useGetHistoryEntryApiHistoryHistoryEntryIdGetQuery,
  useListDeliveriesApiDeliveriesGetQuery,
  useGetDeliveryApiDeliveriesSourceDeliveryIdGetQuery,
  useUpdateDeliveryApiDeliveriesSourceDeliveryIdPutMutation,
  useListCommentsApiCommentsGetQuery,
  useCreateCommentApiCommentsPostMutation,
  useGetCommentApiCommentsCommentIdGetQuery,
  useUpdateCommentApiCommentsCommentIdPutMutation,
  useDeleteCommentApiCommentsCommentIdDeleteMutation,
  useRootGetQuery,
  useHealthHealthGetQuery,
} = injectedRtkApi;
